import { createApp } from 'vue'
import { store } from './store';
import App from './App.vue'
import {createI18n} from 'vue-i18n';
import router from './router';
import { IonicVue } from '@ionic/vue';
import BaseLayout from './components/BaseLayout.vue';
import './delete-app-plugin/web';
import './delete-app-plugin/definitions';
import {reloadManifestMetaData, savePreferences, getMinRequiredDataForAppLoad} from '@/storage/DataStorage';
import VueMarkdownIt from 'vue3-markdown-it';
import {getLocaleMessages} from "@/storage/DataStorage";
import Loading from "@/MainLoader.vue";
import {logInfo, logError} from "@/utilities/Filesystem";
import { LiveUpdateService } from './services/LiveUpdateService';
import { getJourneySetMetadata } from '@/services/AWSService';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { Capacitor } from '@capacitor/core';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './theme/custom.css';
import {getImageDataUri} from "@/utilities/Compatibility";
import {SplashScreen} from '@capacitor/splash-screen';

//This is to fix a Safari bug with indexedDB - https://www.techradar.com/news/apple-safari-update-is-causing-chaos-with-web-apps
window.indexedDB;
store.dispatch("setCheckForVersion", true);
const loadingApp = createApp(Loading);
loadingApp.use(store);
window.document.getElementById("spinnerContainer")?.remove();
SplashScreen.hide();
loadingApp.mount('#app');
logInfo('[Loading] 10% - app-init', 0);
store.dispatch("setLoadingValue", { value: 0.1, slug: "app-init" });

if(!store.getters.isPwa()){
    LiveUpdate.ready();
}
const app = createApp(App);
app.use(store);
app.use(IonicVue, {swipeBackEnabled:false});
app.use(router);
app.use(VueMarkdownIt);

app.component('base-layout', BaseLayout);

const setHomeRoute = async () => {
    logInfo('[Loading] 90% - set-home-route-start', 0);
    store.dispatch("setLoadingValue", { value: 0.9, slug: "set-home-route-start" });
    logInfo("Debug (90%): SetHomeRoute START",1);
    logInfo("Debug (90%): SetHomeRoute , getting locale from store",2);
    
    let messages = store.getters.getLocaleMessages();
    logInfo("Debug (90%): SetHomeRoute , got messages, checking if application exists",2);
    
    if (!messages.application) {
        logInfo("Debug (90%): SetHomeRoute , messages.application isn't present , loading from db",2);
        const languageData = await getLocaleMessages();
        logInfo("Debug (90%): SetHomeRoute , loaded language data from db",2);

        await store.dispatch("setLocale", languageData.locale);
        await store.dispatch("setMessages", languageData.messages);
        messages = languageData.messages;
        logInfo("Debug (90%): SetHomeRoute , dispatched locale and messages",2);
    } else {
        logInfo("Debug (90%): SetHomeRoute , messages.application exists, skipping db load",2);
    }
    
    logInfo("Debug (90%): SetHomeRoute , getting preferences",2);
    const preferences = store.getters.getPreferences();
    logInfo("Debug (90%): SetHomeRoute , checking if first time opening app",2);
    const isFirstTimeOpeningApp = store.getters.isFirstTimeOpeningApp();

    logInfo("Debug (90%): SetHomeRoute , setting header image",2);
    await store.dispatch("setHeaderImage", getImageDataUri(messages.application.homeScreenIcon));
    logInfo("Debug (90%): SetHomeRoute , getting build configs",2);
    const { pinScreenOn, alternatePinScreen }  = messages.buildConfigs.config;
    
    if (isFirstTimeOpeningApp) {
        logInfo("Debug (90%): SetHomeRoute , first time opening app - setting preferences",2);
        preferences.shouldUseSafetyCalculator = pinScreenOn === "true";
        preferences.simpleCategoryShapes = messages.buildConfigs.config.simpleCategoryShapes === "true";
        preferences.isRelease = messages.language.isRelease === "true";
        await savePreferences(preferences);
        logInfo("Debug (90%): SetHomeRoute , first time preferences saved",2);
    } else {
        logInfo("Debug (90%): SetHomeRoute , NOT first time - updating preferences",2);
        preferences.simpleCategoryShapes = messages.buildConfigs.config.simpleCategoryShapes === "true";
        preferences.isRelease = messages.language.isRelease === "true";
        preferences.allowNotes = messages.buildConfigs.config.allowNotes !== undefined ? messages.buildConfigs.config.allowNotes === "true" : true;
        preferences.allowAudioInnotes = messages.buildConfigs.config.allowAudioInnotes !== undefined ? messages.buildConfigs.config.allowAudioInnotes === "true" : true;
        logInfo("Debug (90%): SetHomeRoute , saving preferences to DB",2);
        await savePreferences(preferences);
        // TEMPORARILY DISABLED: Manifest download disabled for debugging iPhone crash
        // The crash correlates strongly with network activity
        logInfo("Debug (90%): SetHomeRoute , SKIPPING manifest metadata reload for debugging",2);
        // await reloadManifestMetaData();
        logInfo("Debug (90%): SetHomeRoute , waiting 150ms",2);
        
        // Wait for IndexedDB transactions to complete before navigation to prevent router hang
        await new Promise(resolve => setTimeout(resolve, 150));
        logInfo("Debug (90%): SetHomeRoute , 150ms wait complete",2);
    }
    
    logInfo("Debug (90%): SetHomeRoute , adding routes",2);
    if (alternatePinScreen === "true")
        router.addRoute({
            path: "/calculator",
            name: "calculator",
            component: () => import("@/views/Calculator/Calculator_Alt.vue"),
        });
    if (preferences.shouldUseSafetyCalculator) {
        router.addRoute({
            path: "/",
            name: "home",
            redirect: "/calculator"
        });
    }
    else {
        store.dispatch("setShouldShowCalculatorIcon", false);
        router.addRoute({
            path: "/",
            name: "home",
            redirect: "/fields"
        });
    }

    logInfo("Debug (90%): SetHomeRoute , routes added, performing navigation",2);
    if (isFirstTimeOpeningApp || preferences.shouldUseSafetyCalculator) {
        logInfo("Debug (90%): SetHomeRoute , navigating to home (first time or calculator)",2);
        await router.replace({name: "home"});
    } else {
        logInfo(`Debug (90%): SetHomeRoute , navigating to lastVisitedRoute: ${preferences.lastVisitedRoute}`,2);
        await router.replace({path: `${preferences.lastVisitedRoute}`});
    }
    logInfo("Debug (90%): SetHomeRoute COMPLETE",1);
}

router.isReady().then(async () => {
    // Log immediately with [Loading] format to ensure it shows in filtered console
    logInfo('[Loading] 11% - router-ready', 0);
    store.dispatch("setLoadingValue", { value: 0.11, slug: "router-ready" });

    // PWA only: set platform and fetch app icon from S3 (subdomain = variant) so the loading screen can show it
    if (Capacitor.getPlatform() === 'web') {
        await store.dispatch('setPlatform');
        const liveUpdateService = new LiveUpdateService();
        const offlineOnlyWeb = await liveUpdateService.getPackagedOfflineOnlySecure();
        await store.dispatch('setOfflineOnlySecure', offlineOnlyWeb);
        await liveUpdateService.downloadAndSaveAppIcon();
    }

    // Load language from local ONLY first (no network). PWA gets language from S3 (e.g. via router/guard).
    if (!store.getters.isPwa()) {
        logInfo('[Loading] 11% - loading-language-local-only', 0);
        await getMinRequiredDataForAppLoad();
        const liveUpdateForOfflineFlag = new LiveUpdateService();
        const offlineOnlyNative = await liveUpdateForOfflineFlag.getPackagedOfflineOnlySecure();
        await store.dispatch('setOfflineOnlySecure', offlineOnlyNative);
        logInfo(`[Loading] 11% - offline-only-secure-${offlineOnlyNative}`, 0);
        logInfo('[Loading] 11% - language-loaded-secure-known', 0);

        // Non-secure only: restore app icon if missing (e.g. after bundle apply, OS cache trim, reinstall).
        // Secure apps NEVER hit the network without explicit user approval — they fall back to globalAppIcon.
        if (!store.getters.isSecure()) {
            const nativeLiveUpdateService = new LiveUpdateService();
            nativeLiveUpdateService.restoreAppIconIfMissing().catch(e =>
                logError(e, '[main.ts] restoreAppIconIfMissing failed')
            );
        }
    }

    // Check for pending or new bundle updates BEFORE navigation for non-PWA apps
    if(!store.getters.isPwa()){ 
        logInfo('[Loading] 12% - checking-updates-native', 0);
        store.dispatch("setLoadingValue", { value: 0.12, slug: "checking-updates-native" });
        
        logInfo(`[Loading] 12% - updates-enabled`, 0);
        
        if (store.getters.shouldSkipBundleUpdates()) {
            logInfo('[Loading] 12% - EARLY-SKIP-ACTIVE', 0);
            store.dispatch("setLoadingValue", { value: 0.12, slug: "EARLY-SKIP-ACTIVE" });
            // Skip all update operations
        } else {
        
        const liveUpdateService = new LiveUpdateService();
        
        // SECOND: Quick check if we should skip everything (skip flag from previous apply)
        // This check happens BEFORE loop detection to break the loop faster
        let skipAll = false;
        try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            try {
                const result = await Filesystem.readFile({
                    path: 'skipUpdateCheck.flag',
                    directory: Directory.Data,
                    encoding: 'utf8' as any
                });
                const flagContent = result.data as string;
                if (flagContent && flagContent.length > 0) {
                    logInfo(`[Loading] 12% - SKIP-FLAG-FOUND-${flagContent.substring(0, 20)}`, 0);
                    store.dispatch("setLoadingValue", { value: 0.12, slug: "SKIP-FLAG-FOUND" });
                    
                    // Parse timestamp from flag (format: bundleId|timestamp)
                    const parts = flagContent.split('|');
                    const flagTimestamp = parts.length > 1 ? parseInt(parts[1], 10) : 0;
                    const timeSinceFlag = Date.now() - flagTimestamp;
                    
                    // If flag was set in the last 60 seconds, DEFINITELY skip everything
                    if (flagTimestamp > 0 && timeSinceFlag < 60000) {
                        logInfo(`[Loading] 12% - SKIP-FLAG-RECENT-${Math.round(timeSinceFlag/1000)}s`, 0);
                        store.dispatch("setLoadingValue", { value: 0.12, slug: `SKIP-FLAG-RECENT-${Math.round(timeSinceFlag/1000)}s` });
                        skipAll = true;
                    }
                    
                    // Clear the flag regardless
                    await Filesystem.deleteFile({
                        path: 'skipUpdateCheck.flag',
                        directory: Directory.Data
                    });
                    logInfo('[Loading] 12% - skip-flag-cleared', 0);
                }
            } catch (e) {
                // Flag doesn't exist - that's fine
            }
        } catch (fsError) {
            logInfo('[Loading] 12% - fs-import-failed', 0);
        }
        
        if (skipAll) {
            logInfo('[Loading] 15% - SKIPPING-ALL-UPDATES-FLAG', 0);
            store.dispatch("setLoadingValue", { value: 0.15, slug: "SKIPPING-ALL-UPDATES-FLAG" });
            // Skip all update operations - the flag was just cleared above
        } else {
            // SECOND: Check if we're in a restart loop (circuit breaker)
            logInfo('[Loading] 13% - checking-loop', 0);
            store.dispatch("setLoadingValue", { value: 0.13, slug: "checking-loop" });
            const isInLoop = await liveUpdateService.isInRestartLoop();
            
            if (isInLoop) {
                logInfo('[Loading] 14% - LOOP-DETECTED', 0);
                store.dispatch("setLoadingValue", { value: 0.14, slug: "LOOP-DETECTED" });
                // Skip all update operations to break the loop
            } else {
                // Only skip applying a pending bundle when in cooldown (e.g. just applied and reloaded)
                const shouldSkipApply = await liveUpdateService.shouldSkipUpdateCheck();
                logInfo(`[Loading] 15% - shouldSkipApply-${shouldSkipApply}`, 0);
                store.dispatch("setLoadingValue", { value: 0.15, slug: `shouldSkipApply-${shouldSkipApply}` });

                if (!shouldSkipApply) {
                    // Apply pending bundle from previous session if any
                    logInfo('[Loading] 16% - checking-pending', 0);
                    store.dispatch("setLoadingValue", { value: 0.16, slug: "checking-pending" });
                    const appliedPendingBundle = await liveUpdateService.applyPendingBundleIfExists();
                    if (appliedPendingBundle) {
                        logInfo('[Loading] 17% - pending-applied-reloading', 0);
                        store.dispatch("setLoadingValue", { value: 0.17, slug: "pending-applied-reloading" });
                        return;
                    }
                }

                // Non-secure Android/iOS: check for updates on every launch (only skipped by skipAll / isInLoop above)
                const isSecure = store.getters.isSecure();
                logInfo(`[Loading] 18% - isSecure-${isSecure}`, 0);
                store.dispatch("setLoadingValue", { value: 0.18, slug: `isSecure-${isSecure}` });

                if (!isSecure) {
                    logInfo('[Loading] 19% - checking-for-updates', 0);
                    store.dispatch("setLoadingValue", { value: 0.19, slug: "checking-for-updates" });
                    await liveUpdateService.checkForUpdates();
                    logInfo('[Loading] 19% - updates-checked', 0);
                } else {
                    logInfo('[Loading] 19% - secure-skip-auto-update', 0);
                    store.dispatch("setLoadingValue", { value: 0.19, slug: "secure-skip-auto-update" });
                }
            }
        }
        } // Close the else block for shouldSkipBundleUpdates
    } else {
        logInfo('[Loading] 11% - pwa-mode', 0);
        store.dispatch("setLoadingValue", { value: 0.11, slug: "pwa-mode" });
        const liveUpdateService = new LiveUpdateService();
        setTimeout(() => {
            liveUpdateService.checkBundleManifestForContentVersion().catch((error) => {
                logError(error, '[main.ts] PWA bundleManifest check failed');
            });
            getJourneySetMetadata().catch((error) => {
                logError(error, '[main.ts] PWA journeySetMetadata check failed');
            });
        }, 0);
    }
    
    logInfo("[main.ts] Update checks complete, continuing to setHomeRoute", 0);
    
    // Wait for IndexedDB operations to complete before navigation
    logInfo("Waiting 500ms for database operations to settle before navigation", 1);
    await new Promise(resolve => setTimeout(resolve, 500));
    logInfo("Database settle wait complete, proceeding to setHomeRoute", 1);

    await setHomeRoute();
    
    //logInfo("Router is ready: " + new Error().stack, 2);
    const locale = store.getters.getLocale();
    const messages = store.getters.getLocaleMessages();
    const i18n = createI18n({
        legacy: false,
        locale: locale,
        messages: messages
    });
    app.use(i18n);
    logInfo('[Loading] 100% - app-ready', 0);
    store.dispatch("setLoadingValue", { value: 1, slug: "app-ready" });

    // change from default of white after loaded 
    // (to keep the mobile device boot screen white until loaded)
    window.document.body.style.backgroundColor = "var(--color-app-excess-background)";
    
    app.mount('#app');
    
    // NOTE: Background manifest download is now ONLY triggered when app regains focus
    // (in App.vue's appStateChange listener). Running it on first launch was causing
    // WebView crashes on iPhones due to heavy database operations during initialization.
}).catch((err: unknown) => {
    const e = err instanceof Error ? err : new Error(String(err));
    const details: string[] = [e.message || '(no message)', e.name || 'Error'];
    if (e.stack) details.push(e.stack);
    if (typeof (err as any)?.code !== 'undefined') details.push(`code=${(err as any).code}`);
    if (typeof (err as any)?.name === 'string') details.push(`name=${(err as any).name}`);
    console.error('[main.ts] router.isReady/startup failed:', details.join(' | '));
    logError(e, '[main.ts] router.isReady/startup failed');
    store.dispatch("setLoadingValue", { value: -1, slug: "startup-error" });
});
